import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  signInWithEmailAndPassword, 
  signInWithPopup, 
  signInWithRedirect,
  getRedirectResult,
  browserLocalPersistence,
  setPersistence
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
// Native MUI Components
import { 
  Button, 
  TextField, 
  Container, 
  Paper, 
  Box, 
  Typography, 
  Avatar 
} from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/hooks/useI18n';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingRedirect, setIsCheckingRedirect] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useI18n();

  // Handle redirect result on component mount
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          // User successfully signed in via redirect
          navigate('/');
        }
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : t('pages.login.unknownError');
        toast({
          title: t('pages.login.googleLoginError'),
          description: errorMessage,
          variant: 'destructive',
        });
      } finally {
        setIsCheckingRedirect(false);
      }
    };

    handleRedirectResult();
  }, [navigate, toast, t]);

  // Set persistence to local storage for better user experience
  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch((error) => {
      // Silent fail - persistence is not critical, user can still log in
      if (error instanceof Error) {
        toast({
          title: t('pages.login.loginError'),
          description: t('pages.login.unknownError'),
        });
      }
    });
  }, [toast, t]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('pages.login.unknownError');
      toast({
        title: t('pages.login.loginError'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    try {
      // Detect if user is on a mobile device
      // Mobile devices often have issues with popups, so we use redirect flow
      // Note: We use user agent detection (not media queries) because a desktop 
      // browser in small window can still handle popups, but mobile device cannot
      // const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      //   navigator.userAgent
      // );

      const isMobile = false; // Temporarily disable mobile redirect because of missing end to end tests

      if (isMobile) {
        // Use redirect flow for mobile devices (more reliable)
        await signInWithRedirect(auth, googleProvider);
        // Note: The page will redirect, so loading state doesn't need to be reset here
        // The redirect result will be handled in the useEffect above
      } else {
        // Use popup flow for desktop (better UX, stays on page)
        await signInWithPopup(auth, googleProvider);
        setIsLoading(false);
        navigate('/');
      }
    } catch (error: unknown) {
      setIsLoading(false);
      
      // Handle specific error cases using Firebase error codes
      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as { code: string; message: string };
        
        // Check if popup was blocked
        if (
          firebaseError.code === 'auth/popup-blocked' ||
          firebaseError.code === 'auth/unauthorized-domain'
        ) {
          toast({
            title: t('pages.login.popupBlockedError'),
            description: `${t('pages.login.popupBlockedDescription')} (${firebaseError.code})`,
            variant: 'destructive',
          });
        } 
        // Check if user cancelled the login
        else if (
          firebaseError.code === 'auth/cancelled-popup-request' ||
          firebaseError.code === 'auth/popup-closed-by-user' ||
          firebaseError.code === 'auth/user-cancelled'
        ) {
          // User intentionally cancelled, show less alarming message
          toast({
            title: t('pages.login.loginCancelled'),
            description: `${t('pages.login.loginCancelledDescription')} (${firebaseError.code})`,
          });
        } 
        // Generic error with Firebase error code
        else {
          toast({
            title: t('pages.login.googleLoginError'),
            description: `${firebaseError.message} (${firebaseError.code})`,
            variant: 'destructive',
          });
        }
      } else if (error instanceof Error) {
        // Fallback for non-Firebase errors
        toast({
          title: t('pages.login.googleLoginError'),
          description: error.message,
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('pages.login.googleLoginError'),
          description: t('pages.login.unknownError'),
          variant: 'destructive',
        });
      }
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        gap: 2 
      }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 400 }}>
          <Typography variant="h4" component="h2" textAlign="center" gutterBottom>
            {t('pages.login.title')}
          </Typography>
          
          {isCheckingRedirect ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Typography>{t('pages.login.loading')}</Typography>
            </Box>
          ) : (
            <>
              <Box component="form" onSubmit={handleEmailLogin} sx={{ mt: 2 }}>
                <TextField
                  id="email"
                  label={t('pages.login.email')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  required
                  margin="normal"
                />
                <TextField
                  id="password"
                  label={t('pages.login.password')}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  required
                  margin="normal"
                />
                <Button 
                  type="submit" 
                  variant="contained" 
                  fullWidth 
                  sx={{ mt: 3, mb: 2 }}
                  disabled={isLoading}
                >
                  {t('pages.login.emailLoginButton')}
                </Button>
              </Box>
              
              <Button
                onClick={handleGoogleLogin}
                variant="outlined"
                fullWidth
                startIcon={<GoogleIcon />}
                disabled={isLoading}
                sx={{ 
                  color: 'text.primary',
                  borderColor: 'grey.300',
                  '&:hover': {
                    bgcolor: 'grey.50',
                    borderColor: 'grey.400'
                  }
                }}
              >
                {isLoading ? t('pages.login.loading') : t('pages.login.googleLoginButton')}
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
